import { createParser, type EventSourceMessage } from 'eventsource-parser';

/** Incremental wire decoding only. Consumers own payloads and command lifecycle. */
export class SseDecoder {
  private events: EventSourceMessage[] = [];
  private readonly parser = createParser({
    onEvent: (event) => { this.events.push(event); },
  });

  feed(chunk: string): readonly EventSourceMessage[] {
    this.parser.feed(chunk);
    const events = this.events;
    this.events = [];
    return events;
  }
}

/** Does not reconnect or replay a request, and never completes a truncated frame. */
export async function* readSseEvents(
  reader: ReadableStreamDefaultReader<Uint8Array>,
): AsyncGenerator<EventSourceMessage> {
  const text = new TextDecoder();
  const decoder = new SseDecoder();
  for (;;) {
    const { done, value } = await reader.read();
    yield* decoder.feed(done ? text.decode() : text.decode(value, { stream: true }));
    if (done) return;
  }
}

/** Owns a finite response body; completion and request replay remain with the caller. */
export function readSseResponse(response: Response, signal: AbortSignal): AsyncGenerator<EventSourceMessage> {
  if (!response.headers.get('content-type')?.includes('text/event-stream'))
    throw new Error('Sean returned an unexpected response.');
  if (response.body === null) throw new Error('Sean did not return a stream.');
  return readResponseBody(response.body, signal);
}

async function* readResponseBody(body: ReadableStream<Uint8Array>, signal: AbortSignal): AsyncGenerator<EventSourceMessage> {
  const reader = body.getReader();
  const cancel = async (): Promise<void> => {
    try { await reader.cancel(); } catch { /* Cleanup must not replace completed output. */ }
  };
  const abort = (): void => { void cancel(); };
  signal.addEventListener('abort', abort, { once: true });
  try {
    signal.throwIfAborted();
    for await (const event of readSseEvents(reader)) {
      signal.throwIfAborted();
      yield event;
    }
    signal.throwIfAborted();
  } finally {
    signal.removeEventListener('abort', abort);
    await cancel();
    reader.releaseLock();
  }
}
